import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appeler } from '../../api/client.ts';
import { Chargement } from '../../composants/communs.tsx';
import { formaterFcfa } from '../../composants/ui/index.ts';
import type { ClientEncours, ReponseClients } from '../clients/types.ts';

/**
 * Sélection du client — autocomplétion par nom ou téléphone.
 *
 * Une correspondance existe : on la choisit, processus normal. Aucune ne
 * correspond : le nom tapé (et le téléphone facultatif) ne sont que
 * SIGNALÉS au parent via `onSaisie` — jamais créés ici. Taper un nom sans
 * aller au bout de la vente ne doit laisser aucune fiche derrière soi ;
 * seule la validation de la vente (`NouvelleVente`) crée réellement le
 * client, juste avant d'enregistrer la vente elle-même.
 * « Divers » (aucun client) reste le défaut tant que rien n'est choisi.
 */
export function ClientVente({
  client,
  onChoisir,
  onRetirer,
  onSaisie,
}: {
  client: ClientEncours | null;
  onChoisir: (client: ClientEncours) => void;
  onRetirer: () => void;
  /** Nom/téléphone tapés mais pas encore choisis — `null` tant que vide. */
  onSaisie: (saisie: { nom: string; telephone: string } | null) => void;
}): ReactNode {
  const [recherche, setRecherche] = useState('');
  const [telephone, setTelephone] = useState('');

  const terme = recherche.trim();

  const { data: trouves, isFetching } = useQuery({
    queryKey: ['vente-clients', terme],
    queryFn: () => appeler<ReponseClients>(`/clients?q=${encodeURIComponent(terme)}`),
    enabled: terme !== '',
    select: (reponse) => reponse.donnees,
  });

  const aucunResultat = terme !== '' && !isFetching && (trouves?.length ?? 0) === 0;

  // Signalé au parent à chaque changement — c'est lui qui décide, à la
  // validation de la vente, s'il doit vraiment créer ce client.
  useEffect(() => {
    onSaisie(aucunResultat ? { nom: terme, telephone: telephone.trim() } : null);
  }, [aucunResultat, terme, telephone, onSaisie]);

  if (client !== null) {
    return (
      <div className="vente-section">
        <div className="vente-section__tete">
          <h2>Client</h2>
        </div>
        <div className="ligne" style={{ justifyContent: 'space-between' }}>
          <div>
            <strong>
              {client.prenom ?? ''} {client.nom}
            </strong>{' '}
            <span className="entete__sous">
              {client.plafond_credit === null
                ? 'crédit illimité'
                : `disponible ${formaterFcfa(Math.max(0, client.plafond_credit - client.encours))}`}
            </span>
          </div>
          <button type="button" className="bouton--discret" onClick={onRetirer}>
            Retirer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="vente-section">
      <div className="vente-section__tete">
        <h2>Client</h2>
      </div>

      <input
        aria-label="Client — nom ou téléphone"
        placeholder="Client divers — taper un nom ou un téléphone"
        value={recherche}
        onChange={(evenement) => {
          setRecherche(evenement.target.value);
        }}
      />
      <p className="entete__sous" style={{ marginTop: 4 }}>
        Laissé vide, la vente est enregistrée sur « Client divers ».
      </p>

      {isFetching && <Chargement />}

      {(trouves?.length ?? 0) > 0 && (
        <div className="pile" style={{ gap: 'var(--espace-2)', marginTop: 'var(--espace-2)' }}>
          {trouves?.map((candidat) => (
            <button
              key={candidat.id}
              type="button"
              className="bouton--discret"
              style={{ justifyContent: 'space-between', width: '100%' }}
              onClick={() => {
                onChoisir(candidat);
                setRecherche('');
                setTelephone('');
              }}
            >
              <span>
                {candidat.prenom ?? ''} {candidat.nom}
                {candidat.telephone !== null && ` — ${candidat.telephone}`}
              </span>
            </button>
          ))}
        </div>
      )}

      {aucunResultat && (
        <div
          className="pile"
          style={{ gap: 'var(--espace-2)', marginTop: 8, padding: 'var(--espace-3)', background: 'var(--sable-appui)', borderRadius: 'var(--rayon-net)' }}
        >
          <span className="entete__sous">
            Aucun client « {terme} » — sera enregistré sous ce nom à la validation de la vente.
          </span>
          <input
            placeholder="Téléphone (facultatif)"
            value={telephone}
            onChange={(evenement) => {
              setTelephone(evenement.target.value);
            }}
          />
        </div>
      )}
    </div>
  );
}
